import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { CommonModule, JsonPipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { trigger, transition, style, animate } from '@angular/animations';
import { QuantityStepperComponent } from "../../component/layout/element/quantity-stepper.component";
import { Store } from '@ngrx/store';
import { listingsFeature } from '../../store/listings.store';
import { defaultIfEmpty, filter, lastValueFrom, take, takeUntil } from 'rxjs';
import { ListingPublic, ListingVariationPublic } from '../../../../globalTypes/listings.types';
import { ActivatedRoute, Router } from '@angular/router';
import { NameTagComponent } from "../../component/layout/element/name.tag.component";
import { accountFeature } from '../../store/account.store';
import { LocationPublic } from '../../../../globalTypes/account.types';
import { createBlankInitialOrder, CreateBlankInitialOrderVariables, getListing } from '@dataconnect/account';
import { ordersFeature } from '../../store/order.store';
import { ButtonComponent } from "../../component/layout/element/button.component";
import { Logger } from '../../helper/logger';
import { MatSnackBar } from '@angular/material/snack-bar';
import { OrderService } from '../../services/order.service';
import { Base } from '../../component/wrapper/base-wrapper.component';
import { LoadingLogoComponent } from "../../component/layout/element/loading-logo.component";
import { authUserFeature } from '../../store/authUser.store';
import { ListingService } from '../../services/listing.service';
import { AddItemToCartVariables } from '../../../../globalTypes/order.types';
import { MustLoginSnackComponent } from '../../component/layout/element/must-login.snack';
import { DIGITAL_PRODUCT_LABEL, LOCATION_LABEL } from '../../../../globalTypes/configs';
const log = new Logger("DisplayListingsComponent");

@Component({
  selector: 'msh-display-listings',
  standalone: true,
  imports: [CommonModule, MatButtonModule, QuantityStepperComponent, NameTagComponent, ButtonComponent, LoadingLogoComponent],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('300ms ease-in', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ opacity: 0 }))
      ])
    ]),
    trigger('scaleIn', [
      transition(':enter', [
        style({ transform: 'scale(0.9)', opacity: 0 }),
        animate('200ms ease-out', style({ transform: 'scale(1)', opacity: 1 }))
      ])
    ])
  ],
  template: `
<div class=" max-w-7xl mx-auto p-4 text-copy">
  <!-- Set grid dimensions  -->
  <div class="flex flex-col md:grid md:grid-cols-[130px_1fr_350px] gap-6">
    
    <!-- Mobile: Item details at top -->
    <div class="md:hidden space-y-2">
      <ng-container *ngTemplateOutlet="title;context:{listing: listing}"></ng-container>
    </div>

    <!-- #1 Thumbnail tray -->
    <div class="flex md:flex-col gap-2 overflow-x-auto md:overflow-y-auto md:overflow-x-hidden">
      @if(listing){
        @for (thumb of listing.listingImages_on_listing; track thumb; let idx = $index) {
          <img
            [src]="thumb.image.url"
            (click)="selectImage(idx)"
            [class]="'w-32 h-32 object-cover rounded-lg border-4 transition-colors cursor-pointer ' + (selectedImageIdx === idx ? ' border-navy-400' : ' border-transparent hover:border-orange-500')"
          />
        }
      }
    </div>
    <!-- #2 Main image -->
    <div class="flex justify-center items-start">

      @if(listing && listing !== undefined
      && listing.listingImages_on_listing
      && listing.listingImages_on_listing[selectedImageIdx]
      && listing.listingImages_on_listing[selectedImageIdx].image
      && listing.listingImages_on_listing[selectedImageIdx].image.url){
        <img
          [@fadeInOut]
          [src]="listing.listingImages_on_listing[selectedImageIdx].image.url"
          class="max-w-full max-h-96 rounded-lg object-contain"
        />
      } @else if(!listing || !listing.listingImages_on_listing){
        <msh-loading-logo [@fadeInOut] [class]="' w-96 h-96 object-contain ' + (inPopup ? 'absolute' : '')"></msh-loading-logo>
      } @else {
        <img
          [@fadeInOut]
          [src]="'/MobileStorehouse_logo_landscape.png'"
          class="max-w-full opacity-25 max-h-96 rounded-lg object-contain"
        />
      }
    </div>

    <!-- #3 Item details (desktop only) -->
    <div class="hidden md:flex flex-col space-y-2">
      @if(listing){
        <ng-container *ngTemplateOutlet="title;context:{listing: listing}"></ng-container>
        <div class="grow"></div>
        <!-- #4 Variations & Add to Cart -->
        <div class="mt-6">
          @if(listingVariations && listingVariations.length > 0){
            <ng-container *ngTemplateOutlet="variations;context:{variations:listingVariations}"></ng-container>
          <ng-container *ngTemplateOutlet="addToCart;context:{max:listingVariations[selectedVariationIdx].quantity, addToCartFunc:addToCartFunc.bind(this)}"></ng-container>
          }
        </div>
      }
    </div>
  </div>

  <!-- #4 Variations & Add to Cart (mobile only) -->
  <div class="md:hidden mt-6">
      @if(listingVariations && listingVariations.length > 0){
        <div class="flex flex-col gap-2 overflow-x-auto">
          <ng-container *ngTemplateOutlet="variations;context:{variations:listingVariations}"></ng-container>
        </div>
        <ng-container *ngTemplateOutlet="addToCart;context:{max:listingVariations[selectedVariationIdx].quantity, addToCartFunc:addToCartFunc.bind(this)}"></ng-container>
      }
  </div>
</div>
<ng-template #title let-listing="listing" >
  @if(listing){
    <h1 class="text-xl font-bold text-heavy-copy">{{ listing.name }}</h1>
    <p class="text-sm text-navy/50">{{ listing.description }}</p>
    @if(locations[listing.locationId]; as loc){
      <msh-name-tag (click)="viewListingLocationProfile()" class=" cursor-pointer transition-colors hover:text-orange-500 block text-copy mt-3 mb-7" [size]="'small'" [label]="loc.name || 'no name'" [url]="loc.profileImage?.url"></msh-name-tag>
    }
    <div class="text-lg text-orange-600 pt-4 block font-semibold">{{ (listingVariations[selectedVariationIdx].priceInCents || 0) / 100 | currency}}</div>
    
    <!-- Extra Details -->
    @if (details && details.length) {
      <ul class="mt-1 text-sm text-gray-600 list-disc pl-4">
        @for (detail of details; track detail) {
          <li>{{ detail }}</li>
        }
      </ul>
    }
  }
</ng-template>
<ng-template #variations let-variations="variations">
 @if(variations.length > 1){
    <p class="font-light mb-2">Choose a variation: </p>
    @if(variations[selectedVariationIdx]; as va){
      <div class=""><span class="font-bold">{{va.name}}</span> 
      <span class="text-sm ml-10"> @if(isDigital){ {{ DIGITAL_PRODUCT_LABEL }} }@else{ {{va.quantity}} in stock}</span></div>
      <div>{{va.description}}</div>
    }
    <div class="flex gap-2 mt-2 overflow-x-auto">
      @for (variation of variations; track variation.id; let idx = $index) {
        @if(variation.image?.url; as url){
          <img
            [src]="url"
            (click)="selectVariation(idx)"
            [@scaleIn]
            [class]="'w-20 h-20 object-cover rounded-lg border-4 transition-colors cursor-pointer'
             + ( selectedVariationIdx === idx ? ' border-navy' : ' border-transparent hover:border-orange-500')
             + ( variationHasNoStock[idx] ? ' opacity-50 cursor-not-allowed' : '')
             "
          />
        }
      }
    </div>
  }
</ng-template>
<ng-template #addToCart let-max="max" let-addToCartFunc="addToCartFunc">
  <div class="flex gap-4 mt-4">
    @if(isMyListing){
      @if(inPopup){
        <msh-button class="w-full" [stealthMode]="true" (onClick)="viewListing()" [cta]="'View Listing'"></msh-button>
      }
      <msh-button class="w-full" (onClick)="editListing()" [cta]="'Edit Listing'"></msh-button>
      
    } @else {
      <msh-quantity-stepper class="text-copy" [max]="max" [min]="variationHasNoStock[selectedVariationIdx] ? 0 : 1" [(value)]="quantity"></msh-quantity-stepper>
      <msh-button
        class="w-full"
        [isLoading]="addingToCart"
        [disabled]="quantity < 1 || !listing.listingVariations_on_listing || !listing.listingVariations_on_listing || (variationAlreadyInCartQuantity > 0 && variationAlreadyInCartQuantity === quantity)"
        (onClick)="addToCartFunc($event)"
        [cta]="variationAlreadyInCartQuantity ? 'Update Quantity':'Add to Cart'"
      >
      </msh-button>
    }
  </div>
</ng-template>
  `,
  styles: [`
    .text-navy-900 { color: #0a1f44; }
    .bg-navy-900 { background-color: #0a1f44; }
    .hover\\:bg-navy-700:hover { background-color: #132f63; }
  `]
})
export class DisplayListingsComponent extends Base<DisplayListingsComponent> implements OnChanges, OnInit {
  @Input() listingId!: string;
  @Input() inPopup = false;
  @Output() onActionTaken: EventEmitter<string> = new EventEmitter();
  selectedImageIdx = 0;
  selectedVariationIdx = 0;
  quantity = 1;
  listing!: ListingPublic;
  listingVariations: ListingPublic['listingVariations_on_listing'] = [];
  variationHasNoStock: {[idx: number]: boolean} = {};
  locations: {[locationId: string]: LocationPublic} = {}
  addingToCart: boolean = false;
  isMyListing = false;
  details: string[] = [];
  variationAlreadyInCartQuantity: number = 0;
  isDigital = false;
  DIGITAL_PRODUCT_LABEL = DIGITAL_PRODUCT_LABEL

  constructor(
    private store: Store,
    private route: ActivatedRoute,
    private orderService: OrderService,
    private snackBar: MatSnackBar,
    private router: Router,
    private listingService: ListingService,
  ){
    super();
  }

  async ngOnChanges(changes: any): Promise<void> {
    if(!changes['listingId']) return;
    this.store.select(listingsFeature.selectListings).pipe(takeUntil(this.$destroy)).subscribe(async listings=>{
      const listing = listings.find(l=>l&&l.id === this.listingId)
      if(listing){
        this.listing = listing as ListingPublic
      }else{
        if(!this.listingId) throw Error('No listingId available')
        const listingRef = await getListing({listingId: this.listingId});
        if(listingRef.data.listings.length > 0){
          this.listing = listingRef.data.listings[0] as ListingPublic;
          console.log({msg:'got it here ', listingRef})
        }
      }
      this.isDigital = this.listing.category?.isDigital || false;
      this.listingVariations = this.listing.listingVariations_on_listing || []
      this.listingVariations.forEach((v,idx)=>{
        if(v && ((v.quantity && v.quantity > 0) || this.isDigital)){
          this.variationHasNoStock[idx] = false;
        }else{
          this.variationHasNoStock[idx] = true;
          if(this.selectedVariationIdx === idx){
            this.selectedVariationIdx++;
          }
        }
      })
      console.log({listing, listingVariations: this.listingVariations, len: listing?.listingVariations_on_listing?.length})
      const shippingCostText = this.listingService.getShippingCostText(this.listing);
      if (shippingCostText) {
        this.details.push(shippingCostText);
      }
      const user = await lastValueFrom(this.store.select(authUserFeature.selectUser).pipe(filter(l=>!!l), defaultIfEmpty(null), take(1)));
      if(user && user.defaultLocationId === this.listing.locationId){
        this.isMyListing = true;
      }
    })
    this.locations = await lastValueFrom(this.store.select(accountFeature.selectLocations).pipe(take(1)))
  }

  async ngOnInit(): Promise<void> {
    this.matchQuantityToCart();
  }

  selectImage(idx: number) {
    this.selectedImageIdx = idx;
  }

  selectVariation(idx: number) {
    this.variationAlreadyInCartQuantity = 0;
    this.selectedVariationIdx = idx;
    this.matchQuantityToCart();
  }

  async matchQuantityToCart(){
    const variation = this.listing.listingVariations_on_listing[this.selectedVariationIdx];
    if(!variation || !variation.id) return;
    const listingVariationId: string = variation.id;
    this.quantity = 1;
    if(this.variationHasNoStock[this.selectedVariationIdx]){
      this.quantity = 0;
    }
    let cart = await lastValueFrom(this.store.select(ordersFeature.selectCart).pipe(filter(c => !!c),take(1)));
    if(!cart || !cart.id) return;
    cart.orderItems_on_order?.forEach(item => {
      if(item.listingVariation.id === listingVariationId){
        this.quantity = item.quantity;
        this.variationAlreadyInCartQuantity = item.quantity;
      }
    });
  }

  viewListing(){
    this.onActionTaken.emit('addedToCart');
    this.router.navigate(['listing', 'details', this.listingId])
  }

  editListing(){
    this.onActionTaken.emit('addedToCart');
    this.router.navigate(['manage-listing', 'details', this.listing.id]);
  }

  viewListingLocationProfile(){
    this.onActionTaken.emit('addedToCart');
    this.router.navigate(['profile', 'listings', this.listing.locationId]);
  }

  async addToCartFunc(ev: any){
    this.addingToCart = true;
    const location = await lastValueFrom(this.store.select(accountFeature.selectLocation).pipe(take(1)));
    if(!location) {
      const snackbarRef = this.snackBar.openFromComponent(MustLoginSnackComponent, {data: {}, duration: 5000});
      const dismissed = await lastValueFrom( snackbarRef.afterDismissed().pipe(take(1)));
      console.log({dismissed});
      if(dismissed.dismissedByAction){
        this.router.navigate(['login']);
      }
      this.addingToCart = false;
      return;
    }
    let cart = await lastValueFrom(this.store.select(ordersFeature.selectCart).pipe(take(1)));
    if(!cart || !cart.id || cart.id === "new"){
      if(!location) throw new Error(`Lost connection to ${LOCATION_LABEL}: No ${LOCATION_LABEL} id found.`)
      const vars: CreateBlankInitialOrderVariables = { locationId: location.id}
      log.log({vars})
      try {
        const newCartRef = await createBlankInitialOrder(vars);
        log.log({newCartRef})
        cart = newCartRef.data.cart;
      } catch(error){
        this.addingToCart = false;
        log.error({error})
        this.snackBar.open("Error creating initial cart", "close", {duration: 8000})
      }
    }
    log.log({cart})
    // update db cart with thing
    if(!cart || !cart.id || cart.id === undefined) {
      this.addingToCart = false;
      throw new Error("Could not create new cart");
    }
    const variation = this.listing.listingVariations_on_listing[this.selectedVariationIdx];
    if(!variation || !variation.id || variation.id === undefined || !this.listing.locationId) {
      this.addingToCart = false;
      throw new Error("Could not detect listing variation details");
    }
    const addToCartVars: AddItemToCartVariables = {
      quantity: this.quantity,
      fulfilledToLocationId: location.id,
      fulfilledByLocationId: this.listing.locationId,
      orderId: cart.id,
      listingVariationId: variation.id,
    }
    log.log({addToCartVars});
    try {
      await this.orderService.addItemToCartAndHydrate(addToCartVars, cart);
    } catch(error){
      this.addingToCart = false;
      log.error({error})
      this.snackBar.open("Error updating cart with this item", "close", {duration: 8000})
    }
    this.addingToCart = false;
    this.onActionTaken.emit('addedToCart');
    // go to cart page
  }
}
